import { useState, useCallback } from "react";
import groupsApi from "../API/groupsApi";

export const useGroupsApi = (showNotificationArg) => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState(null);
  const [operators, setOperators] = useState([]);
  const [clients, setClients] = useState([]);
  // Use passed showNotification or fallback to local (for backward compatibility)
  let showNotification = showNotificationArg;
  if (!showNotification) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    showNotification = require("./useNotification").default().showNotification;
  }

  // Get all groups
  const fetchGroups = useCallback(
    async (search = "", clientFilter = []) => {
      setLoading(true);
      try {
        const response = await groupsApi.getAll(search, clientFilter);
        setGroups(response.data || []);
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error fetching groups:", error);
        showNotification("Error fetching groups", "error");
        return {
          success: false,
          error: error.response?.data?.message || "Failed to fetch groups",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Get group by ID
  const fetchGroupById = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const response = await groupsApi.getById(id);
        setGroup(response.data);
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error fetching group details:", error);
        showNotification("Error fetching group details", "error");
        return {
          success: false,
          error:
            error.response?.data?.message || "Failed to fetch group details",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Create new group
  const createGroup = useCallback(
    async (groupData) => {
      setLoading(true);
      try {
        const response = await groupsApi.create(groupData);
        showNotification("Group created successfully", "success");
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error creating group:", error);
        const errorMessage =
          error.response?.data?.message || "Error creating group";
        showNotification(errorMessage, "error");
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Update group
  const updateGroup = useCallback(
    async (id, groupData) => {
      setLoading(true);
      try {
        const response = await groupsApi.update(id, groupData);
        showNotification("Group updated successfully", "success");
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error updating group:", error);
        const errorMessage =
          error.response?.data?.message || "Error updating group";
        showNotification(errorMessage, "error");
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Delete group
  const deleteGroup = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const response = await groupsApi.delete(id);
        showNotification("Group deleted successfully", "success");
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error deleting group:", error);
        const errorMessage =
          error.response?.data?.message || "Error deleting group";
        showNotification(errorMessage, "error");
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Fetch operators for assignment
  const fetchOperators = useCallback(async () => {
    try {
      const response = await groupsApi.getOperators();
      setOperators(response.data || []);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error fetching operators:", error);
      showNotification("Error fetching operators", "error");
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch operators",
      };
    }
  }, [showNotification]);

  // Fetch clients for assignment
  const fetchClients = useCallback(async () => {
    try {
      const response = await groupsApi.getClients();
      setClients(response.data || []);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error fetching clients:", error);
      showNotification("Error fetching clients", "error");
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch clients",
      };
    }
  }, [showNotification]);

  // Get operators by group ID
  const fetchOperatorsByGroupId = useCallback(
    async (groupId) => {
      try {
        const response = await groupsApi.getOperatorsByGroupId(groupId);
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error fetching operators by group ID:", error);
        showNotification("Error fetching operators", "error");
        return {
          success: false,
          error: error.response?.data?.message || "Failed to fetch operators",
        };
      }
    },
    [showNotification]
  );

  // Clear group state
  const clearGroup = useCallback(() => {
    setGroup(null);
  }, []);

  // Clear all state
  const clearState = useCallback(() => {
    setGroups([]);
    setGroup(null);
    setOperators([]);
    setClients([]);
  }, []);

  return {
    // State
    loading,
    groups,
    group,
    operators,
    clients,

    // Actions
    fetchGroups,
    fetchGroupById,
    createGroup,
    updateGroup,
    deleteGroup,
    fetchOperators,
    fetchClients,
    fetchOperatorsByGroupId,
    clearGroup,
    clearState,
  };
};

export default useGroupsApi;
